// Redis-like caching implementation using IndexedDB for browser storage
// This provides persistent caching across browser sessions

interface CacheItem<T> {
  data: T;
  timestamp: number;
  expiresAt: number;
  key: string;
}

class IndexedDBCache {
  private dbName = 'pochoclo-cache';
  private dbVersion = 1;
  private storeName = 'cache-store';
  private db: IDBDatabase | null = null;

  async init(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.dbVersion);
      
      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };
      
      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains(this.storeName)) {
          const store = db.createObjectStore(this.storeName, { keyPath: 'key' });
          store.createIndex('expiresAt', 'expiresAt', { unique: false });
        }
      };
    });
  }

  async set<T>(key: string, data: T, ttlMs: number): Promise<void> {
    if (!this.db) await this.init();
    
    const item: CacheItem<T> = {
      key,
      data,
      timestamp: Date.now(),
      expiresAt: Date.now() + ttlMs,
    };

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      const request = store.put(item);
      
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve();
    });
  }

  async get<T>(key: string): Promise<T | null> {
    if (!this.db) await this.init();
    
    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.storeName], 'readonly');
      const store = transaction.objectStore(this.storeName);
      const request = store.get(key);
      
      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        const item = request.result as CacheItem<T>;
        
        if (!item) {
          resolve(null);
          return;
        }
        
        // Check if expired
        if (Date.now() > item.expiresAt) {
          this.delete(key); // Clean up expired item
          resolve(null);
          return;
        }
        
        resolve(item.data);
      };
    });
  }

  async delete(key: string): Promise<void> {
    if (!this.db) await this.init();
    
    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      const request = store.delete(key);
      
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve();
    });
  }

  async cleanup(): Promise<void> {
    if (!this.db) await this.init();
    
    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      const index = store.index('expiresAt');
      const range = IDBKeyRange.upperBound(Date.now());
      const request = index.openCursor(range);
      
      request.onerror = () => reject(request.error);
      request.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest).result;
        if (cursor) {
          cursor.delete();
          cursor.continue();
        } else {
          resolve();
        }
      };
    });
  }

  async clear(): Promise<void> {
    if (!this.db) await this.init();
    
    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      const request = store.clear();
      
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve();
    });
  }
}

// Global cache instance
export const persistentCache = new IndexedDBCache();

// Cache TTL constants (longer periods for better performance)
export const PERSISTENT_CACHE_TTL = {
  TMDB_SEARCH: 7 * 24 * 60 * 60 * 1000, // 7 days (much longer for TMDB data)
  CONTENT_LIST: 2 * 60 * 60 * 1000, // 2 hours (increased from 30 minutes)
  STREAMING_PLATFORMS: 7 * 24 * 60 * 60 * 1000, // 7 days
  USER_PREFERENCES: 30 * 24 * 60 * 60 * 1000, // 30 days (user prefs rarely change)
} as const;

// Initialize cache on module load
if (typeof window !== 'undefined') {
  persistentCache.init().catch(console.error);
  
  // Clean up expired items every hour
  setInterval(() => {
    persistentCache.cleanup().catch(console.error);
  }, 60 * 60 * 1000);
}